import React, {createContext, ReactNode, useContext, useState} from 'react';
import {
    CHAT_MESSAGE_TYPE,
    Message,
    MessageResponse,
    UI_ACTION_TYPE,
    UIAction,
    UICreateContentActionParams,
    UIFilterAction
} from "../types/chatTypes";
import {deleteConversation, sendMessage, startConversation} from "../queries/agentQueries";
import {useAgentExecutor} from "../hooks/ActionCoordinator";
import {CollectionClip} from "@/types/collectionTypes";
import {getSubRoot} from "@/utils/utils";
import router from "@/routes";
import {useClipActiveFilters} from "@/contexts/application/ClipActiveFilters";
import {useKeyMomentActiveFilters} from "@/contexts/application/KeyMomentActiveFilters";
import {usePageSettings} from "@/contexts/PageSettingsContext";

// Estendiamo localmente l'interfaccia Message per includere le scelte del backend
export interface ChatMessage extends Message {
    choices?: string[][];
    media?: CollectionClip[];
    actions?: UIAction[];
}

interface ChatContextType {
    messages: ChatMessage[];
    conversationId: string | null;
    isOpen: boolean;
    isThinking: boolean;
    activeChoices: string[][] | null;
    isServiceUnavailable: boolean; // <--- NUOVO STATO EXPORTATO
    sendChatMessage: (message: string) => Promise<void>;
    openConversation: (openNew: false) => Promise<void>;
    closeConversation: () => void;
    executeDelayedMessagesActions : (id: string) => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

interface ChatProviderProps {
    children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [conversationId, setConversationId] = useState<string | null>(null);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isThinking, setIsThinking] = useState<boolean>(false);
    const [activeChoices, setActiveChoices] = useState<string[][] | null>(null);
    const [isServiceUnavailable, setIsServiceUnavailable] = useState<boolean>(false); // <--- NUOVO STATO LOCALE
    const { executeActions } = useAgentExecutor();


    const {setClipActiveFilters} = useClipActiveFilters();
    const {setKeyMomentActiveFilters} = useKeyMomentActiveFilters()
    const {setCollectionGridShowed} = usePageSettings()

    const addMessage = (type: CHAT_MESSAGE_TYPE, content: string, choices?: string[][], media?: CollectionClip[], actions?: UIAction[]) => {
        const newMessage: ChatMessage = {
            id: crypto.randomUUID(),
            type,
            content,
            timestamp: new Date(),
            choices,
            media, // Clips, Cards, ...
            actions
        };
        setMessages((prev) => [...prev, newMessage]);
    };

    const executeDelayedMessagesActions = (id: string) => {
        const msg = messages.find(ms => ms.id === id)
        if(msg) {
            msg.actions?.forEach(a => {
                if (a.type === UI_ACTION_TYPE.CREATE_CONTENT) {
                    const basePath = getSubRoot(window.location.pathname)
                    const targetPath = `${basePath}/clips`
                    const actionParams = a.params as UICreateContentActionParams
                    setClipActiveFilters(actionParams.filter_by_str)
                    setCollectionGridShowed('clip')
                    router.navigate(targetPath);
                }
                if(a.type === UI_ACTION_TYPE.FILTER) {
                    const basePath = getSubRoot(window.location.pathname)
                    const actionParams = a.params as UIFilterAction
                    if (actionParams.resource_type === 'key_moment') {
                        const targetPath = `${basePath}/keymoments/library`
                        setKeyMomentActiveFilters(actionParams.filter_by_str)
                        router.navigate(targetPath);
                    }
                    if (actionParams.resource_type === 'logic_clip') {
                        const targetPath = `${basePath}/clips`
                        setClipActiveFilters(actionParams.filter_by_str)
                        setCollectionGridShowed('clip')
                        router.navigate(targetPath);
                    }
                }
            })
        }
    }

    const sendChatMessage = async (message: string) => {
        if (isServiceUnavailable) return; // Protezione extra a livello di funzioni

        addMessage(CHAT_MESSAGE_TYPE.USER, message);
        setIsThinking(true);
        setActiveChoices(null);

        try {
            const response: MessageResponse = await sendMessage(conversationId as string, message);

            executeActions(response.actions)

            const hasChoices = response.choices && response.choices.length > 0;

            addMessage(CHAT_MESSAGE_TYPE.AGENT, response.reply, hasChoices ? response.choices : undefined, response.media?.length !== 0 ? response.media : undefined, response.actions);

            if (hasChoices) {
                setActiveChoices(response.choices);
            }
        } finally {
            setIsThinking(false);
        }
    };

    const openConversation = async (openNew: false) => {
        setIsOpen(true);
        setIsServiceUnavailable(false);

        try {
            if (conversationId && openNew) {
                await closeConversation();
                const response = await startConversation();
                setConversationId(response.conversation_id);
            }
            if (!conversationId) {
                const response = await startConversation();
                setConversationId(response.conversation_id);
                addMessage(CHAT_MESSAGE_TYPE.AGENT, response.message);
            }
        } catch (error: any) {
            console.error("Error opening conversation:", error);

            console.log("p1", error.status);
            console.log("p2", error?.response?.statuse);

            const status = error?.status || error?.response?.status;

            if (status === 503) {
                setIsServiceUnavailable(true);
                addMessage(
                    CHAT_MESSAGE_TYPE.AGENT,
                    "AI Service unavailable, contact the administrator"
                );
            }
        }
    };

    const closeConversation = async () => {
        try {
            if (conversationId) {
                await deleteConversation(conversationId as string);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setIsOpen(false);
            setMessages([]);
            setConversationId(null);
            setActiveChoices(null);
            setIsServiceUnavailable(false);
        }
    };

    return (
        <ChatContext.Provider
            value={{
                messages,
                conversationId,
                isOpen,
                isThinking,
                activeChoices,
                isServiceUnavailable,
                sendChatMessage,
                openConversation,
                closeConversation,
                executeDelayedMessagesActions
            }}
        >
            {children}
        </ChatContext.Provider>
    );
};

export const useChat = (): ChatContextType => {
    const context = useContext(ChatContext);
    if (context === undefined) {
        throw new Error('useChat must be used within a ChatProvider');
    }
    return context;
};